/**
 * Eligibility & Revenue pulse — layout mirrors canvases/insights-overview-mockups
 * (mode chips → 4 stats → callout → material table → stable → changelog).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { keepPreviousSameEntity } from '@/lib/query-keys';
import { useAuth } from '@/contexts/AuthContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ArrowLeft, ChevronDown, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

type PulseMode = 'within_year' | 'across_years' | 'client';

interface ChangeEvent {
  npi: string;
  clinicianName: string | null;
  source: 'qpp_api' | 'provider_dataset';
  field: string;
  label: string;
  lane?: string | null;
  material?: boolean;
  numeric: boolean;
  before: unknown;
  after: unknown;
  delta: number | null;
  observedAt: string;
  previousAt: string;
  fromYear?: number;
  toYear?: number;
}

interface PulseSummary {
  materialCount: number;
  individualEligFlips: number;
  groupEligFlips: number;
  statusFlips: number;
  revenueMoves: number;
  allowedChargesDelta: number;
}

interface ClientPracticeRow {
  practiceId: number;
  tin: string;
  tinName: string;
  practiceAlias: string | null;
  materialCount: number;
  summary: PulseSummary;
  lastSnapshotAt: string | null;
  npisTracked: number;
  groupEligibleNpis: number;
  individualEligibleNpis: number;
}

interface PulseFeed {
  mode: PulseMode;
  materialEvents?: ChangeEvent[];
  materialTotal?: number;
  summary?: PulseSummary;
  stable?: {
    npisWithQpp: number;
    groupEligibleNpis: number;
    individualEligibleNpis: number;
    smallPracticeSignalCount: number;
  };
  changelogEvents?: ChangeEvent[];
  changelogTotal?: number;
  events?: ChangeEvent[];
  totalEvents?: number;
  npisTracked?: number;
  snapshotCount?: number;
  firstSnapshotAt?: string | null;
  lastSnapshotAt?: string | null;
  yearsCompared?: number[];
  practices?: ClientPracticeRow[];
}

const MODES: Array<{ id: PulseMode; label: string }> = [
  { id: 'within_year', label: 'Within year' },
  { id: 'across_years', label: 'Across years' },
  { id: 'client', label: 'Client rollup' },
];

function useEligibilityPulse(
  practiceId: number | string | undefined,
  year: number,
  mode: PulseMode,
) {
  return useQuery<PulseFeed>({
    queryKey: ['mvp', 'eligibility-changes', practiceId ?? '', year, mode],
    queryFn: () =>
      apiClient.get(
        `/api/mvp/${practiceId}/eligibility-changes?year=${year}&mode=${mode}`,
      ),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<PulseFeed>(
      2,
      `${practiceId ?? ''}:${mode}`,
    ),
  });
}

const fmtBoolish = (v: unknown) => {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'Eligible' : 'Not eligible';
  if (v === true || v === 'true') return 'Eligible';
  if (v === false || v === 'false') return 'Not eligible';
  return String(v);
};

const fmtMoney = (v: unknown) =>
  v == null ? '—' : `$${Math.round(Number(v)).toLocaleString()}`;

function laneLabel(e: ChangeEvent): string {
  if (e.lane === 'individual' || e.field === 'individual_eligible') return 'Individual';
  if (e.lane === 'group' || e.field === 'group_eligible') return 'Group';
  if (e.lane === 'status') return 'Status';
  if (e.lane === 'revenue' || e.numeric) return 'Revenue';
  return e.source === 'provider_dataset' ? 'Revenue' : 'Other';
}

function changeText(e: ChangeEvent): string {
  if (e.field === 'individual_eligible' || e.field === 'group_eligible') {
    return `${fmtBoolish(e.before)} → ${fmtBoolish(e.after)}`;
  }
  if (e.field === 'allowed_charges') {
    return `Allowed charges ${fmtMoney(e.before)} → ${fmtMoney(e.after)}`;
  }
  if (e.field === 'beneficiaries') {
    return `Beneficiaries ${e.before ?? '—'} → ${e.after ?? '—'}`;
  }
  if (typeof e.before === 'boolean' || typeof e.after === 'boolean') {
    const b = e.before == null ? '—' : e.before ? 'Yes' : 'No';
    const a = e.after == null ? '—' : e.after ? 'Yes' : 'No';
    return `${e.label}: ${b} → ${a}`;
  }
  return `${e.label}: ${e.before ?? '—'} → ${e.after ?? '—'}`;
}

function impactText(e: ChangeEvent): string {
  if (e.field === 'individual_eligible') {
    if (e.after === true) return 'Can report as individual again';
    if (e.after === false) return 'May lose individual reporting path';
  }
  if (e.field === 'group_eligible') {
    if (e.after === true) return 'Group reporting path available';
    if (e.after === false) return 'Group eligibility lost for matched org';
  }
  if (e.lane === 'status') return 'Special-status / reweight signal changed';
  if (e.field === 'allowed_charges' && e.delta != null) {
    const sign = e.delta >= 0 ? '+' : '-';
    return `${sign}$${Math.abs(Math.round(e.delta)).toLocaleString()} estimate basis`;
  }
  if (e.field === 'beneficiaries') return 'Beneficiary volume moved';
  return e.label;
}

function whoScope(e: ChangeEvent): string {
  if (e.field === 'group_eligible' && !e.clinicianName) return 'TIN (matched org)';
  const name = e.clinicianName?.trim();
  if (name) return `${name} · ${e.npi}`;
  return e.npi;
}

function ModeChips({
  mode,
  onChange,
}: {
  mode: PulseMode;
  onChange: (m: PulseMode) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {MODES.map(m => (
        <button
          key={m.id}
          type="button"
          onClick={() => onChange(m.id)}
          className={cn(
            'rounded-lg px-3.5 py-2 text-sm font-medium transition-colors',
            mode === m.id
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80',
          )}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function MaterialTable({
  events,
  showYear,
}: {
  events: ChangeEvent[];
  showYear?: boolean;
}) {
  if (events.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No material changes — refreshing CMS with identical data produces zero rows.
        The first snapshot of the year is the baseline, not a change.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 pr-3 font-medium">When</th>
            {showYear && <th className="py-2 pr-3 font-medium">Years</th>}
            <th className="py-2 pr-3 font-medium">Who / scope</th>
            <th className="py-2 pr-3 font-medium">Lane</th>
            <th className="py-2 pr-3 font-medium">Change</th>
            <th className="py-2 pr-2 font-medium">Impact</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => {
            const lane = laneLabel(e);
            const tone =
              e.field === 'individual_eligible' || e.field === 'group_eligible'
                ? e.after === false
                  ? 'bg-amber-50/50'
                  : e.after === true
                    ? 'bg-emerald-50/40'
                    : undefined
                : undefined;
            return (
              <tr
                key={`${e.npi}-${e.field}-${e.observedAt}-${i}`}
                className={cn('border-b', tone)}
              >
                <td className="py-2.5 pr-3 whitespace-nowrap">
                  {new Date(e.observedAt).toLocaleDateString()}
                </td>
                {showYear && (
                  <td className="py-2.5 pr-3 whitespace-nowrap text-xs">
                    {e.fromYear != null && e.toYear != null
                      ? `${e.fromYear} → ${e.toYear}`
                      : '—'}
                  </td>
                )}
                <td className="py-2.5 pr-3">{whoScope(e)}</td>
                <td className="py-2.5 pr-3">
                  <Badge variant="outline" className="font-normal">
                    {lane}
                  </Badge>
                </td>
                <td className="py-2.5 pr-3 font-medium">{changeText(e)}</td>
                <td className="py-2.5 pr-2 text-muted-foreground">{impactText(e)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function EligibilityChangesReport() {
  const navigate = useNavigate();
  const { selectedPractice, setSelectedPractice, selectedClient } = useAuth();
  const { programYear } = useProgramYear();
  const practiceId = selectedPractice?.id;
  const [mode, setMode] = useState<PulseMode>('within_year');
  const [changelogOpen, setChangelogOpen] = useState(false);

  const feedQ = useEligibilityPulse(practiceId, programYear, mode);
  const feed = feedQ.data;
  const cold = !feed && feedQ.isPending;
  const err = feedQ.isError;

  const materialEvents = useMemo(() => {
    const raw = feed?.materialEvents ?? feed?.events ?? [];
    // If API is old (no summary), still filter to material-ish fields client-side
    if (feed?.summary) return raw;
    const materialFields = new Set([
      'individual_eligible',
      'group_eligible',
      'small_practice',
      'hospital_based',
      'non_patient_facing',
      'hpsa_rural',
      'asc_based',
      'allowed_charges',
      'beneficiaries',
    ]);
    return raw.filter(e => materialFields.has(e.field) || e.material);
  }, [feed]);

  const summary: PulseSummary = feed?.summary ?? {
    materialCount: materialEvents.length,
    individualEligFlips: materialEvents.filter(e => e.field === 'individual_eligible').length,
    groupEligFlips: materialEvents.filter(e => e.field === 'group_eligible').length,
    statusFlips: materialEvents.filter(e => e.lane === 'status' || [
      'small_practice', 'hospital_based', 'non_patient_facing', 'hpsa_rural', 'asc_based',
    ].includes(e.field)).length,
    revenueMoves: materialEvents.filter(e =>
      e.field === 'allowed_charges' || e.field === 'beneficiaries').length,
    allowedChargesDelta: materialEvents
      .filter(e => e.field === 'allowed_charges' && e.delta != null)
      .reduce((s, e) => s + (e.delta ?? 0), 0),
  };

  const changelog = feed?.changelogEvents ?? [];
  const chargesLabel =
    summary.allowedChargesDelta === 0
      ? '$0'
      : `${summary.allowedChargesDelta >= 0 ? '+' : '-'}$${Math.abs(
          Math.round(summary.allowedChargesDelta),
        ).toLocaleString()}`;

  const subtitle = useMemo(() => {
    const name =
      selectedPractice?.practice_alias || selectedPractice?.tin_name || 'Practice';
    const parts = [`${name} · PY ${programYear}`];
    if (feed?.lastSnapshotAt) {
      parts.push(`Last CMS refresh ${new Date(feed.lastSnapshotAt).toLocaleDateString()}`);
    }
    if (feed?.firstSnapshotAt && summary.materialCount > 0) {
      parts.push(
        `${summary.materialCount} material change${summary.materialCount === 1 ? '' : 's'} since ${new Date(feed.firstSnapshotAt).toLocaleDateString()} baseline`,
      );
    } else if (feed && summary.materialCount === 0) {
      parts.push('No material changes since baseline');
    }
    return parts.join(' · ');
  }, [selectedPractice, programYear, feed, summary.materialCount]);

  if (!practiceId) {
    return (
      <div className="p-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>Select a practice to view eligibility changes.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" className="mt-0.5" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Eligibility & revenue</h1>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
      </div>

      <ModeChips mode={mode} onChange={setMode} />

      {err && (
        <Alert variant="destructive">
          <AlertTitle>Could not load pulse</AlertTitle>
          <AlertDescription>
            Restart the API if you just pulled these changes (`docker-compose restart api`),
            then hard-refresh.
          </AlertDescription>
        </Alert>
      )}

      {cold ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : mode === 'client' ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Client rollup</CardTitle>
            <CardDescription>
              Practices you can see under{' '}
              {selectedClient?.client_alias ?? 'this client'} — same-client only, never
              cross-client.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(feed?.practices ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No visible practices.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-3 font-medium">Practice</th>
                      <th className="py-2 pr-3 font-medium">Material</th>
                      <th className="py-2 pr-3 font-medium">Ind / group flips</th>
                      <th className="py-2 pr-3 font-medium">Group-elig NPIs</th>
                      <th className="py-2 pr-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {(feed?.practices ?? []).map(p => (
                      <tr key={p.practiceId} className="border-b">
                        <td className="py-2.5 pr-3">
                          <div className="font-medium">{p.practiceAlias || p.tinName}</div>
                          <div className="text-xs text-muted-foreground">TIN {p.tin}</div>
                        </td>
                        <td className="py-2.5 pr-3 tabular-nums">{p.materialCount}</td>
                        <td className="py-2.5 pr-3 tabular-nums">
                          {p.summary.individualEligFlips} / {p.summary.groupEligFlips}
                        </td>
                        <td className="py-2.5 pr-3 tabular-nums">{p.groupEligibleNpis}</td>
                        <td className="py-2.5 pr-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                              const fromClient = selectedClient?.practices?.find(
                                pr => pr.id === p.practiceId,
                              );
                              setSelectedPractice(
                                fromClient ?? {
                                  id: p.practiceId,
                                  tin: p.tin,
                                  tin_name: p.tinName,
                                  practice_alias: p.practiceAlias ?? undefined,
                                },
                              );
                              setMode('within_year');
                            }}
                          >
                            Open pulse
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Material changes
                {mode === 'across_years' && feed?.yearsCompared?.length
                  ? ` (${feed.yearsCompared.join(' → ')})`
                  : ` (PY ${programYear})`}
              </CardTitle>
              <CardDescription>
                Newest first · individual + group eligibility · special status · revenue.
                Identical CMS refreshes produce zero rows.
              </CardDescription>
              <div className="flex flex-wrap gap-2 pt-1">
                <Badge
                  variant="outline"
                  className={cn(
                    'font-normal',
                    summary.materialCount > 0
                      ? 'border-amber-300 bg-amber-50 text-amber-900'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-800',
                  )}
                >
                  {summary.materialCount} material
                </Badge>
                <Badge variant="outline" className="font-normal">
                  {summary.individualEligFlips} individual / {summary.groupEligFlips} group flips
                </Badge>
                <Badge variant="outline" className="font-normal">
                  {summary.statusFlips} status
                </Badge>
                <Badge variant="outline" className="font-normal">
                  {chargesLabel} charges move
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <MaterialTable events={materialEvents} showYear={mode === 'across_years'} />
            </CardContent>
          </Card>

          {mode === 'within_year' && feed?.stable && (
            <p className="text-xs text-muted-foreground">
              Stable at latest snapshot: {feed.stable.groupEligibleNpis} group-eligible NPIs ·{' '}
              {feed.stable.individualEligibleNpis} individually eligible ·{' '}
              {feed.stable.smallPracticeSignalCount} small-practice signals ·{' '}
              {feed.npisTracked ?? 0} clinicians tracked
              {feed.snapshotCount != null ? ` · ${feed.snapshotCount} snapshots` : ''}
            </p>
          )}

          {mode === 'within_year' && (
            <Collapsible open={changelogOpen} onOpenChange={setChangelogOpen}>
              <div className="flex flex-wrap items-center gap-3">
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    {changelogOpen ? 'Hide' : 'Open'} field changelog
                    <ChevronDown
                      className={cn('h-3.5 w-3.5 transition-transform', changelogOpen && 'rotate-180')}
                    />
                  </Button>
                </CollapsibleTrigger>
                <span className="text-xs text-muted-foreground">
                  Full before/after log including specialty, org name, and data-availability gaps
                  {feed?.changelogTotal != null ? ` · ${feed.changelogTotal} events` : ''}
                </span>
              </div>
              <CollapsibleContent className="mt-3">
                <Card>
                  <CardContent className="pt-4">
                    {changelog.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        No field-level diffs yet (need at least two fetches per clinician).
                      </p>
                    ) : (
                      <MaterialTable events={changelog} />
                    )}
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          )}
        </>
      )}
    </div>
  );
}
